import {
  Award,
  Building,
  Coins,
  Home,
  Key,
  TrendingUp,
  Wallet,
} from "lucide-react";
import type React from "react";
import { MainCard } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { ComparisonResult } from "../types";
import { ComparativeCharts } from "./ComparativeCharts";
import { LeadCaptureForm } from "./LeadCaptureForm";
import { RentVsBuyDesktopTable } from "./RentVsBuyDesktopTable";
import { RentVsBuyMobileList } from "./RentVsBuyMobileList";
import { WealthEvolutionChart } from "./WealthEvolutionChart";

const AMORTIZATION_LABELS: Record<number, string> = {
  0: "SAC (Parcelas decrescentes)",
  1: "Price (Parcelas fixas)",
};

export const RentVsBuyResponse: React.FC<{ results: ComparisonResult }> = ({
  results,
}) => {
  const { summary, monthlyData, simulationData, params } = results;
  const totalMonths = monthlyData.length - 1;
  const years = Math.round(totalMonths / 12);
  const amortizationLabel =
    AMORTIZATION_LABELS[params.amortizationSystemType] || "SAC";
  const bestChoiceLabel =
    summary.bestChoice === "financing"
      ? "Financiar e investir"
      : "Alugar e investir";
  const isFinancingWinner = summary.bestChoice === "financing";
  const isRentingWinner = summary.bestChoice === "renting";

  return (
    <MainCard
      noPadding
      className="mt-8 animate-in fade-in p-4 slide-in-from-bottom-4 duration-500"
    >
      <div className="flex justify-center mb-10">
        <div className="flex flex-col items-center gap-3 w-full max-w-lg">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-widest">
            De acordo com as nossas contas, a melhor opção é
          </p>
          <div className="w-full flex items-center justify-center mt-4">
            <Award className="w-7 h-7 shrink-0 text-primary" />
            <h2 className="text-2xl md:text-3xl font-bold text-primary">
              {bestChoiceLabel}
            </h2>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {/* Card 1: So financiando */}
        <div className="border rounded-2xl p-6 bg-red-50/50 border-red-200 shadow-sm relative overflow-hidden flex flex-col h-full">
          <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
            <Building size={80} className="text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-red-700 mb-6 flex items-center gap-2">
            <Home className="w-5 h-5" />
            Só financiando
          </h3>
          <div className="space-y-5 flex-1">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Patrimônio total
              </p>
              <p className="text-2xl font-bold text-red-700">
                R$ {normalSanatizerBRL(summary.finalPropertyValue)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Valor do imóvel em {years} anos
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Total pago financiando
              </p>
              <p className="text-xl font-bold text-red-700">
                R$ {normalSanatizerBRL(summary.totalPaidFinancing)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Usando a tabela {amortizationLabel}
              </p>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-red-200/50">
            <p className="text-sm font-medium text-gray-600 mb-1">
              Total aportado
            </p>
            <p className="text-xl font-bold text-red-700">R$ 0,00</p>
            <p className="text-xs text-gray-500 mt-1">
              Neste caso você não aporta nada
            </p>
          </div>
        </div>

        {/* Card 2: Financiando e investindo */}
        <div
          className={`border rounded-2xl p-6 bg-green-50/50 border-green-200 relative overflow-hidden flex flex-col h-full transition-all duration-300 ${isFinancingWinner ? "shadow-xl shadow-green-200/60 ring-2 ring-green-500 ring-offset-2 scale-[1.02]" : "shadow-sm"}`}
        >
          {isFinancingWinner && (
            <span className="absolute top-3 left-3 bg-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <Award className="w-3 h-3" />
              Melhor opção
            </span>
          )}
          <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
            <TrendingUp size={80} className="text-green-500" />
          </div>
          <h3
            className={`text-lg font-bold text-green-700 flex items-center gap-2 ${isFinancingWinner ? "mt-7 mb-4" : "mb-6"}`}
          >
            <Wallet className="w-5 h-5" />
            Financiando e investindo
          </h3>
          <div className="space-y-5 flex-1">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Patrimônio total
              </p>
              <p className="text-2xl font-bold text-green-700">
                R$ {normalSanatizerBRL(summary.finalWealthFinancing)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Valor do imóvel + investimentos em {years} anos
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Total pago financiando
              </p>
              <p className="text-xl font-bold text-green-700">
                R$ {normalSanatizerBRL(summary.totalPaidFinancing)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Usando a tabela {amortizationLabel}
              </p>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-green-200/50">
            <p className="text-sm font-medium text-gray-600 mb-1">
              Total aportado
            </p>
            <p className="text-xl font-bold text-green-700">
              R$ {normalSanatizerBRL(summary.totalInvestedFinancing)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              A soma dos aportes financiando
            </p>
          </div>
        </div>

        {/* Card 3: Alugando e investindo */}
        <div
          className={`border rounded-2xl p-6 bg-yellow-50/50 border-yellow-200 relative overflow-hidden flex flex-col h-full transition-all duration-300 ${isRentingWinner ? "shadow-xl shadow-yellow-200/60 ring-2 ring-yellow-500 ring-offset-2 scale-[1.02]" : "shadow-sm"}`}
        >
          {isRentingWinner && (
            <span className="absolute top-3 left-3 bg-yellow-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <Award className="w-3 h-3" />
              Melhor opção
            </span>
          )}
          <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
            <Coins size={80} className="text-yellow-500" />
          </div>
          <h3
            className={`text-lg font-bold text-yellow-700 flex items-center gap-2 ${isRentingWinner ? "mt-7 mb-4" : "mb-6"}`}
          >
            <Key className="w-5 h-5" />
            Alugando e investindo
          </h3>
          <div className="space-y-5 flex-1">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Patrimônio total
              </p>
              <p className="text-2xl font-bold text-yellow-700">
                R$ {normalSanatizerBRL(summary.finalWealthRenting)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Usando uma rentabilidade de {params.rentabilidade}% a.a.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                Total pago de aluguel
              </p>
              <p className="text-xl font-bold text-yellow-700">
                R$ {normalSanatizerBRL(summary.totalPaidRent)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Com o reajuste indicado de {params.igpm}%
              </p>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-yellow-200/50">
            <p className="text-sm font-medium text-gray-600 mb-1">
              Total aportado
            </p>
            <p className="text-xl font-bold text-yellow-700">
              R$ {normalSanatizerBRL(summary.totalInvestedRenting)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              A soma dos aportes alugando
            </p>
          </div>
        </div>
      </div>

      {params.valorImovel - params.entrada >= 100_000 && (
        <LeadCaptureForm
          valorImovel={params.valorImovel}
          entrada={params.entrada}
          prazo={params.prazo}
          scenario={summary.bestChoice}
        />
      )}

      <WealthEvolutionChart monthlyData={monthlyData} />

      {simulationData?.summaryWithAmortizationResult?.totalAmortization && (
        <ComparativeCharts
          simulationData={simulationData}
          hasExtraAmortization={results.hasExtraAmortization}
        />
      )}

      {/* Detalhamento Mensal */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4 px-2">
          Detalhamento Mensal
        </h3>

        {/* Desktop View: Table */}
        <RentVsBuyDesktopTable monthlyData={monthlyData} />

        {/* Mobile View: Cards */}
        <RentVsBuyMobileList monthlyData={monthlyData} />
      </div>
    </MainCard>
  );
};
